import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button, InlineNotification } from '@carbon/react';
import {
  ViewMode_1 as ViewMode1,
  ViewMode_2 as ViewMode2,
} from '@carbon/react/icons';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import { sessionResourceAtom } from 'atoms/root';
import { subscribe } from 'lib/pusher';
import {
  createRoyaltyChecksSearchAtom,
  exportRoyaltyChecksAtom,
  type RoyaltyChecksSearchParams,
} from './atoms';
import { RoyaltyChecksSummaryCards } from './summary-cards';
import { RoyaltyChecksFilters } from './royalty-checks-filters';
import { useRoyaltyCheckRowSubComponent } from './line-items-expansion';
import { royaltyCheckColumns, type RoyaltyCheckRow } from './columns';
import {
  type FlatRow,
  flatColumns,
  flatInitialTableState,
  toFlatRows,
} from './flat-table';
import { columnFiltersToParams, DEFAULT_PARAMS, SORT_FIELD_MAP } from './utils';
import styles from './royalty-checks-content.module.scss';

type RCRow = RoyaltyCheckRow;

// Page size for the flat (one-row-per-line-item) view. Bounds the opt-in
// `includeLineItems` payload so even a `lineItemCount DESC` sort stays under
// Heroku's 30s request limit.
const FLAT_PAGE_SIZE = 50;

interface ExportCompleteEvent {
  export_id: string;
  status: 'complete' | 'failed';
  download_url?: string;
  error?: string;
}

const noopFilterFn = () => true;

const initialTableState: InitialTableState = {
  sortColumn: 'updatedAt',
  sortDirection: 'desc',
};

const RoyaltyChecksContent: FC = () => {
  const { searchAtom } = useMemo(() => createRoyaltyChecksSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const [params, setParams] =
    useState<RoyaltyChecksSearchParams>(DEFAULT_PARAMS);
  const [tableView, setTableView] = useState<'nested' | 'flat'>('nested');
  const [exportError, setExportError] = useState<string | null>(null);

  const session = useAtomValue(sessionResourceAtom);
  const userId = session?.id;
  const exportRoyaltyChecks = useSetAtom(exportRoyaltyChecksAtom);

  const exportSubscriptionRef = useRef<ReturnType<typeof subscribe> | null>(
    null
  );
  // Handler for the in-flight export's completion event. Set just before the
  // export request is fired and cleared once the matching event arrives, so the
  // channel binding (established on mount) can stay live across exports.
  const exportCompleteHandlerRef = useRef<
    ((data: ExportCompleteEvent) => void) | null
  >(null);

  // Idempotently ensure the exports channel is subscribed and bound. Returns
  // false when the user session isn't available yet. Safe to call repeatedly —
  // the binding is established at most once per user.
  const ensureExportSubscription = useCallback(() => {
    if (!userId) return false;
    if (exportSubscriptionRef.current) return true;

    const subscription = subscribe(`user_${userId}_exports`);
    exportSubscriptionRef.current = subscription;
    subscription.bind(
      'royalty_checks_export_complete',
      (data: ExportCompleteEvent) => {
        exportCompleteHandlerRef.current?.(data);
      }
    );
    return true;
  }, [userId]);

  // Subscribe on mount so the channel is established before any export request
  // is fired — otherwise a job that completes before the subscription exists
  // would drop the 'complete' event and the user would never get their
  // download. Cleanup unbinds/unsubscribes on unmount.
  useEffect(() => {
    ensureExportSubscription();

    return () => {
      if (exportSubscriptionRef.current) {
        exportSubscriptionRef.current.unbind('royalty_checks_export_complete');
        exportSubscriptionRef.current.unsubscribe();
        exportSubscriptionRef.current = null;
      }
      exportCompleteHandlerRef.current = null;
    };
  }, [ensureExportSubscription]);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const refetch = useCallback((state: TableState) => {
    const sort = state.sorting[0];

    setParams((curr) => ({
      ...curr,
      ...columnFiltersToParams(state.columnFilters),
      page: state.page,
      pageSize: state.pageSize,
      sortAttribute: sort ? (SORT_FIELD_MAP[sort.id] ?? sort.id) : 'updatedAt',
      sortDirection: sort?.desc === false ? 'ASC' : 'DESC',
      text: state.globalFilter || undefined,
    }));
  }, []);

  const handleGlobalSearchChange = useCallback(
    (state: TableState) => {
      const newText = state.globalFilter || undefined;
      if (params.text === newText) return;
      refetch(state);
    },
    [params.text, refetch]
  );

  const handleFlatFilterChange = useCallback((state: TableState) => {
    setParams((curr) => ({
      ...curr,
      ...columnFiltersToParams(state.columnFilters),
      page: 1,
    }));
  }, []);

  // Switching to the flat view opts the list query into embedding line items
  // (and caps the page size); switching back drops them and restores the
  // nested default page size. Page resets to 1 on both transitions so the
  // server-driven table reconciles cleanly.
  const toggleTableView = useCallback(() => {
    const next = tableView === 'nested' ? 'flat' : 'nested';
    setTableView(next);
    setParams((curr) => ({
      ...curr,
      page: 1,
      includeLineItems: next === 'flat',
      pageSize: next === 'flat' ? FLAT_PAGE_SIZE : DEFAULT_PARAMS.pageSize,
    }));
  }, [tableView]);

  const handleServerExport = useCallback(async () => {
    // Guarantee the subscription is established BEFORE firing the export
    // request, so a fast-completing job can't deliver its event before we're
    // listening. The on-mount effect normally already did this; this call
    // covers the case where the request is triggered before that effect ran.
    if (!ensureExportSubscription()) {
      setExportError('Cannot start export: user session unavailable.');
      return;
    }
    setExportError(null);

    let exportId: string;
    try {
      ({ exportId } = await exportRoyaltyChecks(params));
    } catch (e) {
      setExportError(
        e instanceof Error
          ? e.message
          : 'Failed to start export. Please try again.'
      );
      return;
    }

    // Register the completion handler against the already-live subscription.
    // The channel is bound, so even if the job finishes before this point the
    // event is delivered to the bound listener, which dispatches to whatever
    // handler is set here.
    exportCompleteHandlerRef.current = (data: ExportCompleteEvent) => {
      if (data.export_id !== exportId) return;

      exportCompleteHandlerRef.current = null;

      if (data.status === 'complete' && data.download_url) {
        window.open(data.download_url, '_blank', 'noopener,noreferrer');
      } else {
        setExportError(data.error || 'Export failed. Please try again.');
      }
    };
  }, [ensureExportSubscription, params, exportRoyaltyChecks]);

  const exportPresets = useMemo(
    () => [
      {
        name: 'Export',
        format: 'csv' as const,
        onExport: handleServerExport,
      },
    ],
    [handleServerExport]
  );

  const results = useMemo(
    () => searchResult.data?.results ?? [],
    [searchResult.data?.results]
  );
  const totalCount = searchResult.data?.totalCount ?? 0;

  const renderLineItems = useRoyaltyCheckRowSubComponent<RCRow>();

  const flatRows = useMemo(() => toFlatRows(results), [results]);

  const flatColumnsWithNoopFilter = useMemo(
    () =>
      flatColumns.map<DataTableColumnDef<FlatRow>>((col) => ({
        ...col,
        filterFn: noopFilterFn,
      })),
    []
  );

  const viewModeToggle = (
    <Button
      key="view-mode-toggle"
      kind="ghost"
      size="lg"
      hasIconOnly
      renderIcon={tableView === 'nested' ? ViewMode1 : ViewMode2}
      iconDescription={
        tableView === 'nested'
          ? 'Switch to flat table'
          : 'Switch to nested table'
      }
      tooltipPosition="bottom"
      tooltipAlignment="end"
      onClick={toggleTableView}
    />
  );

  const renderFilterPanel = () => (
    <RoyaltyChecksFilters
      createdByOptions={searchResult.data?.aggregations?.createdByOptions ?? []}
    />
  );

  return (
    <div className={styles.contentWrapper}>
      <RoyaltyChecksSummaryCards statistics={searchResult.data?.aggregations} />

      {exportError && (
        <InlineNotification
          kind="error"
          title={exportError}
          lowContrast
          onCloseButtonClick={() => setExportError(null)}
        />
      )}

      <div className={styles.tableWrapper}>
        {tableView === 'nested' ? (
          <DataTable<RCRow>
            data={results as RCRow[]}
            columns={royaltyCheckColumns}
            getRowId={(row) => String(row.id)}
            initialTableState={initialTableState}
            persistenceKey="royalty-checks-list-table-v2"
            config={{
              columnReordering: false,
              export: { exportPresets },
            }}
            isLoading={searchResult.loading}
            error={searchResult.error ?? null}
            scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
            onPaginationChange={refetch}
            onSortChange={refetch}
            onFilterChange={refetch}
            onGlobalSearchChange={handleGlobalSearchChange}
            searchPlaceholder="Search Royalty Checks"
            renderRowSubComponent={renderLineItems}
            renderFilterPanel={renderFilterPanel}
            leftToolbarActions={[viewModeToggle]}
          />
        ) : (
          <DataTable<FlatRow>
            data={flatRows}
            columns={flatColumnsWithNoopFilter}
            getRowId={(row) => row.id}
            initialTableState={flatInitialTableState}
            persistenceKey="royalty-checks-list-flat-table"
            config={{
              columnReordering: false,
              globalSearch: false,
              export: { exportPresets },
            }}
            isLoading={searchResult.loading}
            error={searchResult.error ?? null}
            onFilterChange={handleFlatFilterChange}
            renderFilterPanel={renderFilterPanel}
            leftToolbarActions={[viewModeToggle]}
          />
        )}
      </div>
    </div>
  );
};

export default RoyaltyChecksContent;
